import React, { useEffect, useState } from "react";
import { fetchV2Schedules } from "../../api/clientV2";
import { useF9Sync } from "./useF9Sync";
import "./UpdateScheduleBadge.css";

interface Props {
  activeVersion: number | null;
  /** Open the Update Schedule flow. */
  onOpenUpdate: () => void;
}

/**
 * Schedule-health badge shown next to the project-delta chip: green when the
 * working schedule matches the forward pass, amber when work is overdue or a
 * reschedule would move dates. Clicking the amber badge opens Update Schedule
 * directly. Computed/cached server-side (services/schedule_update/f9_sync.py).
 */
const UpdateScheduleBadge: React.FC<Props> = ({ activeVersion, onOpenUpdate }) => {
  const [scheduleId, setScheduleId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchV2Schedules()
      .then((s) => {
        if (!cancelled && s.length > 0) setScheduleId(s[0].id);
      })
      .catch(() => {
        /* leave null — badge stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [activeVersion]);

  const f9 = useF9Sync(scheduleId, activeVersion);
  if (f9 === null) return null;

  if (f9.synced) {
    return (
      <span
        className="f9-badge is-synced"
        title="The schedule is current — updating it would change nothing."
      >
        ✓ Up to date
      </span>
    );
  }

  const overdue = f9.overdue_count ?? 0;
  const willMove = f9.pending_count;
  const parts: string[] = [];
  if (overdue > 0) parts.push(`${overdue} overdue`);
  if (willMove > 0) parts.push(`${willMove} to move`);
  return (
    <button
      className="f9-badge is-stale"
      onClick={onOpenUpdate}
      title="Record what happened, then preview and apply the new dates."
    >
      ⚠ <span className="f9-badge-action">Update schedule</span>
      <span className="f9-badge-counts">{parts.join(" · ")}</span>
    </button>
  );
};

export default UpdateScheduleBadge;
